'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, SkipForward } from 'lucide-react';
import type { TourStep } from '../types';

const SPOTLIGHT_PADDING = 8;
const POPUP_GAP = 12;
/** Show centered fallback card if target element isn't found within this time */
const FALLBACK_DELAY_MS = 1200;

interface TourHighlightProps {
  step: TourStep;
  stepNumber?: number;
  totalSteps?: number;
  onNext: () => void;
  isLastStep: boolean;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourHighlight({
  step,
  stepNumber,
  totalSteps,
  onNext,
  isLastStep,
}: TourHighlightProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const scrolledRef = useRef(false);

  const isClickThrough = !!(step.advanceOnNavigate || step.advanceOnElement || step.advanceOnElementRemoved);
  const isInteractive = !!step.allowInteraction;

  const updateRect = useCallback(() => {
    if (!step.targetSelector) return;
    const el = document.querySelector(step.targetSelector);
    if (el) {
      // Element found — hide fallback if it was showing
      setShowFallback(false);
      if (!scrolledRef.current) {
        scrolledRef.current = true;
        el.scrollIntoView({ behavior: 'instant', block: 'center' });
        setTimeout(() => setIsVisible(true), 50);
      }
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [step.targetSelector]);

  // Reset state and manage element animations on step change
  useEffect(() => {
    scrolledRef.current = false;
    queueMicrotask(() => {
      setTargetRect(null);
      setShowFallback(false);
      setIsVisible(false);
    });

    if (!step.animateSelector) return;

    const elements = document.querySelectorAll(step.animateSelector);
    elements.forEach((el) => {
      (el as HTMLElement).classList.add('tour-animate-attention');
    });

    return () => {
      elements.forEach((el) => {
        (el as HTMLElement).classList.remove('tour-animate-attention');
      });
    };
  }, [step.id, step.animateSelector]);

  // Rect tracking (resize/scroll/poll) and fallback timeout
  useEffect(() => {
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    const interval = setInterval(updateRect, 200);

    // Show centered fallback card if target not found after delay
    const fallbackTimeout = setTimeout(() => {
      if (!step.targetSelector) return;
      const el = document.querySelector(step.targetSelector);
      if (!el) {
        setShowFallback(true);
      }
    }, FALLBACK_DELAY_MS);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      clearInterval(interval);
      clearTimeout(fallbackTimeout);
    };
  }, [updateRect, step.targetSelector]);

  // Fallback: show centered card when target element can't be found
  if (!targetRect && showFallback) {
    return createPortal(
      <div role="dialog" aria-modal="false" aria-label={step.title}>
        {/* Dimmed backdrop */}
        <div
          className="fixed inset-0"
          style={{ zIndex: 200, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        />
        {/* Centered card */}
        <div
          className="fixed"
          style={{
            zIndex: 202,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 340,
          }}
        >
          <div className="rounded-xl bg-white shadow-2xl p-5">
            <h3 className="text-base font-semibold mb-1.5 text-gray-900">{step.title}</h3>
            <div className="text-sm leading-relaxed mb-4 text-gray-500">
              {step.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className={index > 0 ? 'mt-3' : ''}>
                  {paragraph}
                </p>
              ))}
            </div>
            <div className="flex items-center justify-between">
              {stepNumber != null && totalSteps != null && (
                <span className="text-xs text-gray-400">
                  {stepNumber} of {totalSteps}
                </span>
              )}
              <button
                onClick={onNext}
                aria-label={isLastStep ? 'Finish tour' : 'Next step'}
                className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
              >
                {isLastStep ? 'Finish' : 'Next'}
                {!isLastStep && <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  if (!targetRect) return null;

  // Spotlight cutout dimensions
  const spotlightStyle: React.CSSProperties = {
    position: 'fixed',
    top: targetRect.top - SPOTLIGHT_PADDING,
    left: targetRect.left - SPOTLIGHT_PADDING,
    width: targetRect.width + SPOTLIGHT_PADDING * 2,
    height: targetRect.height + SPOTLIGHT_PADDING * 2,
    borderRadius: 8,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
    zIndex: 200,
    pointerEvents: 'none',
  };

  // Popover positioning
  const placement = step.placement || 'right';
  const positionInside = step.positionInside || false;
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 202,
    width: 300,
  };

  if (positionInside) {
    popoverStyle.top = targetRect.top + targetRect.height / 2;
    popoverStyle.left = targetRect.left + targetRect.width / 2;
    popoverStyle.transform = 'translate(-50%, -50%)';
  } else if (placement === 'top') {
    popoverStyle.top = targetRect.top - SPOTLIGHT_PADDING - POPUP_GAP;
    popoverStyle.left = targetRect.left + targetRect.width / 2;
    popoverStyle.transform = 'translate(-50%, -100%)';
  } else if (placement === 'right') {
    let topPosition = targetRect.top + targetRect.height / 2;
    const minTopPosition = 150;
    if (topPosition < minTopPosition) topPosition = minTopPosition;
    popoverStyle.top = topPosition;
    popoverStyle.left = targetRect.left + targetRect.width + SPOTLIGHT_PADDING + POPUP_GAP;
    popoverStyle.transform = 'translateY(-50%)';
  } else if (placement === 'left') {
    let topPosition = targetRect.top + targetRect.height / 2;
    const minTopPosition = 150;
    if (topPosition < minTopPosition) topPosition = minTopPosition;
    popoverStyle.top = topPosition;
    popoverStyle.left = targetRect.left - SPOTLIGHT_PADDING - POPUP_GAP;
    popoverStyle.transform = 'translate(-100%, -50%)';
  } else {
    popoverStyle.top = targetRect.top + targetRect.height + SPOTLIGHT_PADDING + POPUP_GAP;
    popoverStyle.left = targetRect.left + targetRect.width / 2;
    popoverStyle.transform = 'translateX(-50%)';
  }

  // Arrow styles
  const arrowStyle: React.CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
  };

  if (positionInside) {
    arrowStyle.display = 'none';
  } else if (placement === 'top') {
    arrowStyle.bottom = -8;
    arrowStyle.left = '50%';
    arrowStyle.transform = 'translateX(-50%)';
    arrowStyle.borderLeft = '8px solid transparent';
    arrowStyle.borderRight = '8px solid transparent';
    arrowStyle.borderTop = '8px solid white';
  } else if (placement === 'right') {
    arrowStyle.top = '50%';
    arrowStyle.left = -8;
    arrowStyle.transform = 'translateY(-50%)';
    arrowStyle.borderTop = '8px solid transparent';
    arrowStyle.borderBottom = '8px solid transparent';
    arrowStyle.borderRight = '8px solid white';
  } else if (placement === 'left') {
    arrowStyle.top = '50%';
    arrowStyle.right = -8;
    arrowStyle.transform = 'translateY(-50%)';
    arrowStyle.borderTop = '8px solid transparent';
    arrowStyle.borderBottom = '8px solid transparent';
    arrowStyle.borderLeft = '8px solid white';
  } else {
    arrowStyle.top = -8;
    arrowStyle.left = '50%';
    arrowStyle.transform = 'translateX(-50%)';
    arrowStyle.borderLeft = '8px solid transparent';
    arrowStyle.borderRight = '8px solid transparent';
    arrowStyle.borderBottom = '8px solid white';
  }

  const renderFooter = () => {
    if (isInteractive) {
      return (
        <div className="flex items-center justify-between">
          {stepNumber != null && totalSteps != null && (
            <span className="text-xs text-gray-400">
              {stepNumber} of {totalSteps}
            </span>
          )}
          <button
            onClick={onNext}
            aria-label="Skip this step"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <SkipForward className="h-3.5 w-3.5" />
            Skip
          </button>
        </div>
      );
    }

    if (isClickThrough) {
      return (
        <div className="flex items-center justify-between">
          {stepNumber != null && totalSteps != null && (
            <span className="text-xs text-gray-400">
              {stepNumber} of {totalSteps}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        {stepNumber != null && totalSteps != null && (
          <span className="text-xs text-gray-400">
            {stepNumber} of {totalSteps}
          </span>
        )}
        <button
          onClick={onNext}
          aria-label={isLastStep ? 'Finish tour' : 'Next step'}
          className="inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium text-white bg-accent-blue hover:brightness-110 transition-all shadow-sm hover:shadow-md"
        >
          {isLastStep ? 'Finish' : 'Next'}
          {!isLastStep && <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      </div>
    );
  };

  return createPortal(
    <div role="dialog" aria-modal="false" aria-label={step.title}>
      {/* Spotlight cutout */}
      <div
        style={{
          ...spotlightStyle,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 150ms ease-in-out',
        }}
      />

      {/* Pulsing blue ring around target for click-through steps */}
      {isClickThrough && (
        <div
          className="tour-click-ring"
          style={{
            position: 'fixed',
            top: targetRect.top - SPOTLIGHT_PADDING,
            left: targetRect.left - SPOTLIGHT_PADDING,
            width: targetRect.width + SPOTLIGHT_PADDING * 2,
            height: targetRect.height + SPOTLIGHT_PADDING * 2,
            zIndex: 201,
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 150ms ease-in-out',
          }}
        />
      )}

      {/* Popover card */}
      <div
        ref={popoverRef}
        style={{
          ...popoverStyle,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 150ms ease-in-out',
        }}
      >
        {/* Arrow */}
        <div style={arrowStyle} />

        <div className="rounded-xl bg-white shadow-2xl p-5">
          <h3 className="text-base font-semibold mb-1.5 text-gray-900">{step.title}</h3>

          <div className="text-sm leading-relaxed mb-4 text-gray-500">
            {step.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className={index > 0 ? 'mt-3' : ''}>
                {paragraph}
              </p>
            ))}
          </div>

          {renderFooter()}
        </div>
      </div>
    </div>,
    document.body,
  );
}
